import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { eventsSchemas } from '../lib/schemas';

/* ── Hero ── */
const HeroSection = styled.section`
  background-color: #7AA6A2;
  padding: 6rem 2rem 7rem;
  text-align: center;
  position: relative;
  z-index: 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 65%;
    height: 100%;
    background-image: url('/img/background.png');
    background-repeat: no-repeat;
    background-size: cover;
    -webkit-mask-image: linear-gradient(to right, transparent 0%, black 50%);
    mask-image: linear-gradient(to right, transparent 0%, black 50%);
    pointer-events: none;
    z-index: -1;
  }
`;

const HeroTitle = styled.h1`
  color: #fff;
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 700;
`;

const HeroWave = styled.div`
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  line-height: 0;
  pointer-events: none;
  svg { width: 100%; height: 64px; display: block; }
`;

/* ── Booking CTA banner ── */
const BookingBanner = styled.section`
  background-color: #7AA6A2;
  padding: 5rem 2rem;
  text-align: center;
`;

const BookingBannerTitle = styled.h2`
  color: #fff;
  font-size: 2rem;
  margin-bottom: 0.75rem;
  font-weight: 700;
  font-family: var(--font-heading);
`;

const BookingBannerText = styled.p`
  color: rgba(255,255,255,0.9);
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
`;

const BookingButton = styled(Link)`
  display: inline-block;
  background-color: #fff;
  color: #7AA6A2;
  font-weight: 700;
  font-size: 1rem;
  padding: 0.75rem 2rem;
  border-radius: 100px;
  transition: background-color 0.2s, color 0.2s;
  text-decoration: none;

  &:hover {
    background-color: #f0f8f7;
    color: #5a8a87;
  }
`;

/* ── Content shell ── */
const ContentSection = styled.section`
  padding: 5.5rem 2rem;
  max-width: 1000px;
  margin: 0 auto;
`;

const SectionLabel = styled.p`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: var(--accent-color);
  margin-bottom: 0.75rem;
  font-weight: 600;
`;

const SectionTitle = styled.h2`
  color: #333;
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  font-family: var(--font-heading);
`;

const SectionSubtitle = styled.p`
  color: #777;
  font-size: 1.05rem;
  margin-bottom: 2.5rem;
  line-height: 1.75;
`;

/* ── Speaking topics cards ── */
const TopicsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 5rem;
`;

const TopicCard = styled.div`
  flex: 0 0 calc(33.333% - 1rem);
  background: #fff;
  border-radius: 12px;
  padding: 1.75rem;
  border: 1px solid #eee;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border-top: 4px solid var(--primary-color);
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
  }

  @media (max-width: 700px) {
    flex: 0 0 calc(50% - 0.75rem);
  }

  @media (max-width: 480px) {
    flex: 0 0 100%;
  }
`;

const TopicIcon = styled.div`
  font-size: 1.75rem;
  margin-bottom: 0.75rem;
`;

const TopicTitle = styled.h3`
  color: #333;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4;
  margin: 0;
  font-family: var(--font-heading);
`;

/* ── Scripture pull quote ── */
const QuoteSection = styled.section`
  background-color: #333;
  padding: 5rem 2rem;
  text-align: center;
`;

const QuoteMark = styled.div`
  font-family: var(--font-heading);
  font-size: 5rem;
  color: var(--accent-color);
  line-height: 0.5;
  margin-bottom: 1.5rem;
  opacity: 0.8;
`;

const QuoteText = styled.blockquote`
  font-family: var(--font-heading);
  font-style: italic;
  font-size: 1.65rem;
  color: #fff;
  max-width: 700px;
  margin: 0 auto 1.25rem;
  line-height: 1.5;
  font-weight: 400;

  @media (max-width: 600px) {
    font-size: 1.25rem;
  }
`;

const QuoteSource = styled.p`
  color: var(--accent-color);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0;
`;

/* ── Upcoming events placeholder ── */
const UpcomingSection = styled.div`
  background-color: #f0f5f5;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 4rem;
  text-align: center;
`;

const UpcomingTitle = styled.h3`
  color: #7AA6A2;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  font-family: var(--font-heading);
`;

const UpcomingSubtext = styled.p`
  color: #646163;
  margin: 0;
`;

/* ── Past events grid ── */
const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const EventCard = styled.div`
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0,0,0,0.08);
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid #eee;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
  }
`;

const EventImagePlaceholder = styled.div`
  width: 100%;
  height: 160px;
  background: linear-gradient(135deg, #7AA6A2 0%, #a8c8c5 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
`;

const EventImageIcon = styled.span`
  font-size: 2rem;
  opacity: 0.85;
`;

const EventImageLabel = styled.span`
  color: rgba(255,255,255,0.85);
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const EventContent = styled.div`
  padding: 1.25rem 1.5rem;
`;

const EventTitle = styled.h3`
  font-size: 1.05rem;
  color: #333;
  margin-bottom: 0.6rem;
  line-height: 1.4;
  font-weight: 700;
`;

const EventDetail = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.2rem;
  line-height: 1.4;
`;

const EventDate = styled.p`
  font-size: 0.85rem;
  color: #7AA6A2;
  font-weight: 600;
  margin-top: 0.6rem;
  margin-bottom: 0;
`;

/* ── Data ── */
const SPEAKING_TOPICS = [
  { icon: '🤖', title: 'AI and the Christian Life: Stewarding Technology with Biblical Wisdom' },
  { icon: '🏗️', title: 'Entrepreneurship as Calling: Building Businesses for God\'s Glory' },
  { icon: '🌍', title: 'Business as a Mission: The Marketplace as Ministry Platform' },
  { icon: '⚖️', title: 'Ethics in Technology: Christian Perspectives on Innovation' },
  { icon: '🏢', title: 'Creating Kingdom Culture in Your Organization' },
];

const Events = () => {
  const pastEvents = [
    {
      title: "AI and the Christian Life – Part 2",
      host: "HarvestNet International",
      event: "Rooted: Grounded in the Authority of Scripture...",
      location: "Lancaster, PA",
      date: "November 12–14, 2025"
    },
    {
      title: "Guest Lecturers: \"Innovation with Technology\"",
      host: "UEH",
      location: "Ho Chi Minh City, Vietnam",
      date: "October 16, 2025"
    },
    {
      title: "Writing, Interneting, and Existing in the Age of AI",
      host: "HackerNoon",
      location: "Ho Chi Minh City, Vietnam",
      date: "August 26, 2025"
    },
    {
      title: "The Future of Artificial Intelligence – Chapter 3",
      host: "LaunchJDI",
      location: "Ho Chi Minh City, Vietnam",
      date: "August 20, 2025"
    },
    {
      title: "AI and the Christian Life",
      host: "Rawlinsville Camp Meeting",
      location: "Holtwood, PA",
      date: "August 4–10, 2025"
    },
    {
      title: "Scarborne, Starborn Showcase",
      host: "TEDxVNUIS",
      location: "Hanoi, Vietnam",
      date: "July 19, 2025"
    },
    {
      title: "Engineered Prompting: Precision GenAI Outputs",
      host: "Saigon A.I.",
      location: "Ho Chi Minh City, Vietnam",
      date: "June 25, 2025"
    },
    {
      title: "Work Smarter with AI",
      host: "CEEC",
      location: "Ho Chi Minh City, Vietnam",
      date: "June 17, 2025"
    },
    {
      title: "Organization Readiness for AI",
      host: "Saigon A.I.",
      location: "Ho Chi Minh City, Vietnam",
      date: "February 27, 2025"
    },
    {
      title: "Guest Lecturer: \"Foundations of AI for STEM\"",
      host: "RMIT",
      location: "Ho Chi Minh City, Vietnam",
      date: "December 2, 2024"
    },
    {
      title: "AI and the Christian Life",
      host: "HarvestNet International",
      event: "MULTIPLY International Summit",
      location: "Lancaster, PA",
      date: "November 13–15, 2024"
    },
    {
      title: "Innovating with Behavioral Data Science",
      host: "The Disruption Is Now",
      location: "Ardmore, PA",
      date: "October 2, 2024"
    },
    {
      title: "AI in Finance Panel Discussion",
      host: "INNOEX",
      location: "Ho Chi Minh City, Vietnam",
      date: "August 22, 2024"
    },
    {
      title: "Forge Your Career Path in Data and AI",
      host: "She Loves Data Vietnam",
      location: "Ho Chi Minh City, Vietnam",
      date: "July 31, 2024"
    },
    {
      title: "Demystifying A.I. for Business People",
      host: "Tectonic Lounge",
      location: "Da Nang, Vietnam",
      date: "July 23, 2024"
    },
    {
      title: "The Future of Artificial Intelligence: Chapter 2",
      host: "LaunchJDI",
      location: "Ho Chi Minh City, Vietnam",
      date: "July 12, 2024"
    }
  ];

  return (
    <Layout
      title="Events"
      description="Book Andrew Schwabe and Rebecca Joy to speak at your conference, church, or retreat. View past speaking engagements."
      path="/events"
      image="/img/og-default.jpg"
      imageAlt="Speaking and events with Andrew Schwabe and Rebecca Joy"
      keywords="book a Christian speaker, faith and AI speaker, conference speaker, church speaker, Andrew Schwabe speaking"
      jsonLd={eventsSchemas(pastEvents)}
    >
      <HeroSection>
        <HeroTitle>Events</HeroTitle>
        <HeroWave>
          <svg viewBox="0 0 1440 64" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,64 C360,0 1080,0 1440,64 L1440,64 L0,64 Z" fill="#FAF9F8" />
          </svg>
        </HeroWave>
      </HeroSection>

      <ContentSection>

        {/* ── Speaking topics ── */}
        <SectionLabel>What We Speak On</SectionLabel>
        <SectionTitle>Speaking Topics</SectionTitle>
        <SectionSubtitle>
          Each talk is tailored to your audience and context — from 20-minute keynotes to full-day workshops.
        </SectionSubtitle>
        <TopicsGrid>
          {SPEAKING_TOPICS.map((t) => (
            <TopicCard key={t.title}>
              <TopicIcon>{t.icon}</TopicIcon>
              <TopicTitle>{t.title}</TopicTitle>
            </TopicCard>
          ))}
        </TopicsGrid>

        {/* ── Upcoming events ── */}
        <SectionLabel>What's Next</SectionLabel>
        <SectionTitle>Upcoming Events</SectionTitle>
        <UpcomingSection>
          <UpcomingTitle>New dates coming soon</UpcomingTitle>
          <UpcomingSubtext>
            Check back here or <a href="mailto:hello@buildingontruth.com">get in touch</a> to find out where we'll be next.
          </UpcomingSubtext>
        </UpcomingSection>

        {/* ── Past events ── */}
        <SectionLabel>Where We've Been</SectionLabel>
        <SectionTitle>Past Speaking Engagements</SectionTitle>
        <EventsGrid>
          {pastEvents.map((event, index) => (
            <EventCard key={index}>
              <EventImagePlaceholder>
                <EventImageIcon>🎤</EventImageIcon>
                <EventImageLabel>Speaking Engagement</EventImageLabel>
              </EventImagePlaceholder>
              <EventContent>
                <EventTitle>{event.title}</EventTitle>
                <EventDetail><strong>Host:</strong> {event.host}</EventDetail>
                {event.event && <EventDetail><strong>Event:</strong> {event.event}</EventDetail>}
                <EventDetail><strong>Location:</strong> {event.location}</EventDetail>
                <EventDate>{event.date}</EventDate>
              </EventContent>
            </EventCard>
          ))}
        </EventsGrid>

      </ContentSection>

      <QuoteSection>
        <QuoteMark>"</QuoteMark>
        <QuoteText>
          Let your light shine before others, that they may see your good deeds and glorify your Father in heaven.
        </QuoteText>
        <QuoteSource>Matthew 5:16</QuoteSource>
      </QuoteSection>

      <BookingBanner>
        <BookingBannerTitle>Bring Us to Your Event</BookingBannerTitle>
        <BookingBannerText>
          Andrew and Rebecca speak at conferences, churches, retreats, and organizations on AI, entrepreneurship, and business as a mission.
        </BookingBannerText>
        <BookingButton to="/contact">
          Request a Speaker →
        </BookingButton>
      </BookingBanner>
    </Layout>
  );
};

export default Events;
